import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import { usePagination } from '@crehana/react-hooks';
import { cleanData, notEmpty } from '@crehana/utils';
import { useRouter } from 'next/router';

import type { FilterSelectOption } from '@/views/AdminDashboard/components/FilterSelect/types';

import { AMPLITUDE } from '../../pages/Metrics/Courses/amplitude';
import { Sort } from '../../pages/Metrics/Courses/types';
import useDebounce from '../useDebounce';
import GET_COURSES_STATISTICS from './graphql/GetCoursesStatics.v4.b2b.graphql';
import GET_COURSES_STATISTICS_TOTALS from './graphql/GetCoursesStaticsTotals.v4.b2b.graphql';
import type {
  GetCoursesStatistics_coursesStatistics_edges_node as CourseNode,
  GetCoursesStatistics,
  GetCoursesStatistics_coursesStatistics_edges,
  GetCoursesStatisticsVariables,
} from './graphql/types/GetCoursesStatistics';
import type {
  GetCoursesStatisticsTotals,
  GetCoursesStatisticsTotalsVariables,
} from './graphql/types/GetCoursesStatisticsTotals';

export const COURSES_FETCH = 15;

const useCoursesStatics = ({
  filter,
  batchSize,
}: {
  filter?: FilterSelectOption;
  batchSize?: number;
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const { debounceValue: searchKey, handleChange } = useDebounce();

  const { slug } = router.query as { slug: string };

  const optionsFixed: FilterSelectOption[] = [
    {
      value: 0,
      label: t('COURSE_LIST_FILTER_1_LABEL'),
      labelOnActive: t('COURSE_LIST_FILTER_1_HINT'),
      isChecked: true,
    },
    {
      value: 1,
      label: t('COURSE_LIST_FILTER_2_LABEL'),
      labelOnActive: t('COURSE_LIST_FILTER_2_HINT'),
      isChecked: false,
    },
    {
      value: 8,
      label: t('COURSE_LIST_FILTER_3_LABEL'),
      labelOnActive: t('COURSE_LIST_FILTER_3_HINT'),
      isChecked: false,
    },
  ];

  const [options] = useState(optionsFixed);
  const [sortBy, setSortBy] = useState<Sort>();
  const [courseType, setCourseType] = useState<number | undefined>(undefined);
  const [sortCount, setSortCount] = useState(0);
  const { data, loading, refetch } = useQuery<
    GetCoursesStatistics,
    GetCoursesStatisticsVariables
  >(GET_COURSES_STATISTICS, {
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
    variables: {
      courseType,
      first: batchSize || COURSES_FETCH,
      organizationSlug: slug,
      searchKey,
      sortBy,
    },
  });

  const { data: rawData } = useQuery<
    GetCoursesStatisticsTotals,
    GetCoursesStatisticsTotalsVariables
  >(GET_COURSES_STATISTICS_TOTALS, {
    context: { clientName: 'v4.b2b' },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-first',
    variables: {
      courseType,
      organizationSlug: slug,
      searchKey,
      sortBy,
    },
  });

  const allIds =
    rawData?.coursesStatistics?.edges
      .map(el => el?.node.originalId as string)
      .filter(notEmpty) || [];

  const pageIds =
    (data &&
      data.coursesStatistics?.edges &&
      cleanData<CourseNode, GetCoursesStatistics_coursesStatistics_edges>(
        data.coursesStatistics.edges,
      )
        .map(el => el?.originalId as string)
        .filter(notEmpty)) ||
    [];

  const items = useMemo(() => {
    if (data?.coursesStatistics?.edges) {
      const cleanedUsers: CourseNode[] = cleanData(
        data.coursesStatistics.edges,
      );
      return cleanedUsers.filter(user => Boolean(user));
    }
    return [];
  }, [data]);

  const totalElements = data?.coursesStatistics?.totalCount || 0;

  const { currentPage, onPageChange, totalPages } = usePagination(
    batchSize || COURSES_FETCH,
    {
      refetch,
      totalElements,
    },
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  const changePage = useCallback(
    (page: number) => {
      onPageChange(page);

      logEvent({
        event: AMPLITUDE.COURSES_PAGINATION,
        data: {
          ...getSourceEventParams(),
          'Destination Page': page,
          'Total Pages': totalPages,
          'Total Courses': data?.coursesStatistics?.totalCount || 0,
          Organization: slug,
        },
      });
    },
    [onPageChange],
  );

  useEffect(() => setCourseType(Number(filter?.value) || undefined), [filter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => onPageChange(1), [searchKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (searchKey && !loading) {
      logEvent({
        event: AMPLITUDE.COURSES_SEARCH,
        data: {
          ...getSourceEventParams(),
          Keyword: searchKey,
          'Total Courses': data?.coursesStatistics?.totalCount || 0,
          Organization: slug,
        },
      });
    }
  }, [searchKey, data, loading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (sortCount > 0) {
      const orderType =
        // eslint-disable-next-line no-nested-ternary
        sortBy === Sort.Asc
          ? 'Ascending'
          : sortBy === Sort.Desc
            ? 'Descending'
            : 'Default';

      logEvent({
        event: AMPLITUDE.COURSES_SORT,
        data: {
          ...getSourceEventParams(),
          'Order Type': orderType,
          Organization: slug,
        },
      });
    }
  }, [sortBy, sortCount]);

  const handleSort = () => {
    const currentClick = sortCount + 1;
    setSortCount(currentClick);

    if (currentClick % 3 == 0) {
      setSortBy(undefined);
      return;
    }

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (sortBy === Sort.Asc) setSortBy(Sort.Desc);
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (sortBy === Sort.Desc || !sortBy) setSortBy(Sort.Asc);
  };

  return {
    allIds,
    pageIds,
    currentPage,
    items,
    handleChange,
    handleSort,
    isLoading: loading,
    changePage,
    options,
    searchKey,
    slug,
    sortBy,
    totalItems: data?.coursesStatistics?.totalCount || 0,
    totalPages,
  };
};

export default useCoursesStatics;
